import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
// This hook runs both as a panel tab (real router) and inside the embedded
// DietPlanDetailPage (synthetic location), so both must resolve correctly.
import {
  useRouteLocation as useLocation,
  useFlowNavigate,
} from "../../../../../common/embeddedFlow/embeddedRoute";
import { Pencil, Trash2, FileText, Copy } from "lucide-react";
import { prefetchTargetPlanDetails } from "../../../core/diet-plan/targetPlanPrefetch";
import NutritionFoodCard from "../../../ui/nutrition/NutritionFoodCard";
import { ApiContext } from "../../../../../context/MyContext";
import { updateVisibleDates, formatDate } from "../../../../../utils/validation.util";
import {
  fetchDailyDietTrackingDetails,
  fetchTargetPlanDetails,
  fetchTargetPlanSummaries,
  fetchTemplateSummaries,
  modifyTargetPlanMealOptions,
  modifyTargetPlanMeals,
  modifyTargetPlans,
  modifyTargetPlanDurations,
  fetchPresetNutrition,
} from "../../../core/api/dietApi";
import { saveTargetPlanAsTemplate } from "../../../core/diet-plan/saveTargetPlanAsTemplate";
import { formatLocalDate, parseLocalDate, toYYYYMMDD } from "../../../../../utils/formatLocalDate";
import { useUtils } from "../../../../../context/UtilsContext";
import { normalizeKey } from "../../../core/utils/normalizeDietKey";
import {
  averageCompactTotalsAcrossOptions,
  buildMealFoodDetailsMap,
  bumpNutritionSummary,
  scaledNutritionMapFromActualTrackings,
  scaledNutritionMapFromTargetPlans,
  sumSummaryCalories,
  summaryHasMacros,
  toCompactNutrientTotals,
  emptyCompactTotals,
} from "../../../core/nutrients/dietStructureNutrition";
import { useMealStructureNutrition } from "../../../core/nutrients/useMealStructureNutrition";
import { nutrientsToDisplayObject } from "../../../core/nutrients/nutrientDisplay";
import { MEALPERIODS } from "../../../core/utils/mealTypes";
import { generateDietPlanPdf } from "../../../core/pdf/dietPlanPdf";
import {
  formatIntakeWindowLabel,
  getMealIntakeWindow,
  hhmmForApi,
} from "../../../core/diet-plan/intakeTime";
import { getUserProfileDetails } from "../../../../../service/AuthWellDhanService";
import { useProfileEventTypeId } from "../eventTypes";
import { useTemplateEventTypeId } from "../../template/eventTypes";
import { useRecipeEventTypeId } from "../../../core/eventTypes";
import {
  getRecipeDetails,
  getRecipeName,
  mealFoodFromTrackingActual,
  mealFoodFromTrackingTarget,
  resolveTrackedFoodDisplay,
} from "../../../core/diet-plan/recipeId";
import { hydrateMissingRecipeDetails } from "../../../core/diet-plan/recipeDetailsCache";
import { flattenTargetPlanSummaries } from "../../../core/diet-plan/flattenTargetPlanSummaries";
import { upsertFoodInMeals, removeFoodFromMeals, findFoodInMeals } from "../../../core/diet-plan/upsertFoodInMeals";
import { formatScheduleDate } from "../../../core/utils/assignedPlansUtils";
import {
  buildMealDataByPeriod,
  buildMealIdMapFromMeals,
  extractAvailableOptionNumbers,
  extractEmptyOptionNumbers,
  findMealOptionsByNumber,
} from "../../../core/diet-plan/mealDataByPeriod";

const DietPlanTabContext = createContext(null);

export function useDietPlanTabContext() {
  const ctx = useContext(DietPlanTabContext);
  if (!ctx) throw new Error('useDietPlanTabContext must be used within DietPlanTab');
  return ctx;
}

export function useDietPlanTab({
  profileId,
  planId: planIdProp,
  initialPlanMeta,
  // Where the plan editor sends the coach when it finishes. Defaults to the
  // full user page; the Users split view overrides it with its own URL so a
  // round trip through the editor lands back on the open panel.
  returnTo: returnToProp,
}) {
  const returnTo = returnToProp || `/dashboard/${profileId}`;
  const isPlanDetailPage = Boolean(planIdProp);
  const [visibleDates, setVisibleDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
  const { get, userId } = useContext(ApiContext);
  const location = useLocation();

  // Store all fetched data from API
  const [allNutritionData, setAllNutritionData] = useState({});
  const [nutritionDataMap, setNutritionDataMap] = useState({});

  // Current filtered data for display
  const [nutritionData, setNutritionData] = useState({
    settings: {
      calories: 0,
      protein: 0,
      carbs: 0,
      fats: 0,
      fiber: 0,
      sugar: 0,
    },
    meals: null,
    totals: null,
    meal_type_totals: null,
  });
  const [plannedData, setPlannedData] = useState({
    meals: null,
    meal_type_totals: null,
    planId: null,
  });

  // The plan detail page knows its plan from the URL, but plannedData.planId is
  // shared with the dashboard's date-driven view and gets cleared whenever that
  // view finds no data for the selected date. Anything that must work on the
  // detail page reads this, never plannedData.planId directly.
  const activePlanId = planIdProp ?? plannedData.planId;

  // Dashboard: Tracked-first — dietitian sees history on landing, then navigates.
  // Plan detail page: stays Planned — its only mode, no tab toggle exists there.
  const [isPlannedView, setIsPlannedView] = useState(isPlanDetailPage);
  const [isLoading, setIsLoading] = useState(true);
  const [isTrackingLoading, setIsTrackingLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [expandedOptions, setExpandedOptions] = useState({});
  const [openTracked, setOpenTracked] = useState({});
  const [openPlanned, setOpenPlanned] = useState({});
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  // Renders the plan editor inline when this tab is inside a surface that can
  // host it (the Users split panel), and routes normally otherwise — so
  // /dashboard/:id behaves exactly as before.
  const navigate = useFlowNavigate();
  const { dietCommonUtils, fetchDietCommonUtils } = useUtils();
  const eventTypeId = useProfileEventTypeId();
  const dietEventTypeId = useTemplateEventTypeId();
  const recipeEventTypeId = useRecipeEventTypeId();

  // Plan edit (same UI as edit template, plan APIs)
  const [planDetails, setPlanDetails] = useState(null);
  const [planDetailsLoading, setPlanDetailsLoading] = useState(false);
  const [selectedOptionFilter, setSelectedOptionFilter] = useState("1");
  const [planViewMode, setPlanViewMode] = useState("options-first"); // "options-first" | "meal-types-first"
  const [selectedMealTypeFilter, setSelectedMealTypeFilter] = useState(null);
  const [addChoiceModal, setAddChoiceModal] = useState({ open: false, title: "", items: [], onSelect: null });
  // Plan meta (title, duration) per planId for cards; populated when plan details load
  const [planMeta, setPlanMeta] = useState({});
  // Edit plan name & dates modal (opened by clicking a plan card)
  const [editPlanModalOpen, setEditPlanModalOpen] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [editPlanName, setEditPlanName] = useState("");
  const [editPlanStartDate, setEditPlanStartDate] = useState("");
  const [editPlanEndDate, setEditPlanEndDate] = useState("");
  const [editPlanSaving, setEditPlanSaving] = useState(false);
  const [editPlanError, setEditPlanError] = useState("");
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfFilename, setPdfFilename] = useState("");
  // Plans table (fetch plan summaries by user) — used in Planned view
  const [plansTableList, setPlansTableList] = useState([]);
  const [plansTableLoading, setPlansTableLoading] = useState(false);
  const [plansTableError, setPlansTableError] = useState("");
  const [planToDelete, setPlanToDelete] = useState(null);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState("");
  const [isDeletingPlan, setIsDeletingPlan] = useState(false);
  const [optionDeleteConfirm, setOptionDeleteConfirm] = useState({ open: false, optionNum: null, message: "" });
  const [isDeletingOption, setIsDeletingOption] = useState(false);
  const [cloningPlanId, setCloningPlanId] = useState(null);
  const [copySourcePlan, setCopySourcePlan] = useState(null);

  // Save-as-template: turn this (possibly customized) plan into a new template
  const [savingAsTemplatePlan, setSavingAsTemplatePlan] = useState(null); // { id, title } or null
  const [savingAsTemplate, setSavingAsTemplate] = useState(false);
  const [saveAsTemplatePhase, setSaveAsTemplatePhase] = useState(null);
  const [saveAsTemplateError, setSaveAsTemplateError] = useState("");
  const [partialTemplateId, setPartialTemplateId] = useState(null);
  const [existingTemplateNames, setExistingTemplateNames] = useState([]);

  // After saving a food item, preserve current plan so filterDataForDate (triggered by fetchAllNutritionData) doesn't switch to another plan
  const planIdToPreserveRef = useRef(null);
  const trackedRecipeHydrationAttemptedRef = useRef(new Set());
  // Target nutrients only change through the edit modal in this UI — one fetch per page load is enough
  const presetNutritionFetchedRef = useRef(false);
  // ±10-day tracking is fetched on first Tracked tab visit (not on Planned-first paint)
  const trackingFetchedRef = useRef(false);
  const trackingProfileRef = useRef(profileId);

  // Client switch: drop cached tracking so the next Tracked visit loads the new user.
  useEffect(() => {
    if (trackingProfileRef.current === profileId) return;
    trackingProfileRef.current = profileId;
    trackingFetchedRef.current = false;
    presetNutritionFetchedRef.current = false;
    setAllNutritionData({});
    setIsTrackingLoading(false);
  }, [profileId]);

  // When opened as plan detail page (URL has planId), set plan and merge list meta (title, dates come from list)
  useEffect(() => {
    if (!planIdProp) return;
    setPlannedData((prev) => ({ ...prev, planId: planIdProp }));
    setIsPlannedView(true);
    if (initialPlanMeta && typeof initialPlanMeta === "object") {
      setPlanMeta((prev) => ({ ...prev, ...initialPlanMeta }));
    }
  }, [planIdProp, initialPlanMeta]);

  // Fetch plan details only on the plan detail page. Use planId from URL (planIdProp) directly.
  useEffect(() => {
    if (!isPlanDetailPage || !planIdProp || !dietEventTypeId || !profileId) {
      if (!isPlanDetailPage) setPlanDetails(null);
      return;
    }
    let cancelled = false;
    setPlanDetailsLoading(true);
    fetchTargetPlanDetails(dietEventTypeId, planIdProp, profileId)
      .then((result) => {
        if (cancelled || !(result?.success && result?.data?.data)) return;
        const data = result.data.data;
        setPlanDetails(data);
        const fromNav = initialPlanMeta?.[planIdProp] || {};
        setPlanMeta((prev) => {
          const existing = prev[planIdProp] || {};
          const durations = data.durations || [];
          const duration =
            (fromNav.durationId || existing.durationId
              ? durations.find(
                  (d) => d.id === (fromNav.durationId || existing.durationId)
                )
              : null) || durations[0];
          return {
            ...prev,
            [planIdProp]: {
              title: fromNav.title ?? existing.title ?? data.title ?? "",
              description: data.description ?? existing.description ?? "",
              durationId: fromNav.durationId ?? existing.durationId ?? duration?.id,
              active_from:
                fromNav.active_from ??
                existing.active_from ??
                duration?.active_from ??
                duration?.start_date,
              active_to:
                fromNav.active_to ??
                existing.active_to ??
                duration?.active_to ??
                duration?.end_date,
            },
          };
        });
      })
      .catch(() => {
        if (!cancelled) setPlanDetails(null);
      })
      .finally(() => {
        if (!cancelled) setPlanDetailsLoading(false);
      });
    return () => { cancelled = true; };
  }, [isPlanDetailPage, planIdProp, dietEventTypeId, profileId, initialPlanMeta]);

  // In Planned view: fetch target plan summaries (no date = all plans for user), flatten to table rows (skip on plan detail page)
  useEffect(() => {
    if (!isPlannedView || isPlanDetailPage || !dietEventTypeId || !profileId) {
      if (!isPlannedView || isPlanDetailPage) {
        setPlansTableList([]);
        setPlansTableError("");
      }
      return;
    }
    let cancelled = false;
    setPlansTableLoading(true);
    setPlansTableError("");
    fetchTargetPlanSummaries(dietEventTypeId, null, null, profileId)
      .then((res) => {
        if (cancelled) return;
        if (res?.success && Array.isArray(res?.data?.data?.target_plans)) {
          setPlansTableList(flattenTargetPlanSummaries(res.data.data.target_plans));
          setPlansTableError("");
        } else {
          setPlansTableList([]);
          setPlansTableError(res?.message || "Failed to load diet plans.");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPlansTableList([]);
          setPlansTableError("Failed to load diet plans. Please try again.");
        }
      })
      .finally(() => {
        if (!cancelled) setPlansTableLoading(false);
      });
    return () => { cancelled = true; };
  }, [isPlannedView, isPlanDetailPage, dietEventTypeId, profileId]);

  const planDataByPeriod = useMemo(
    () => buildMealDataByPeriod(planDetails?.meals, MEALPERIODS),
    [planDetails]
  );

  const planMealIdMap = useMemo(
    () => buildMealIdMapFromMeals(planDetails?.meals),
    [planDetails]
  );

  // Normalized meal type → the target plan meal's intake window in local
  // "HH:MM" ({ start, end }, "" = unset). Shared by every option of the meal.
  const planMealWindows = useMemo(() => {
    const map = {};
    (planDetails?.meals || []).forEach((m) => {
      const k = normalizeKey(m.meal_type_description || m.meal_type || "");
      if (k) map[k] = getMealIntakeWindow(m);
    });
    return map;
  }, [planDetails]);

  const planAvailableOptions = useMemo(
    () => extractAvailableOptionNumbers(planDetails?.meals),
    [planDetails]
  );

  const planEmptyOptions = useMemo(
    () => extractEmptyOptionNumbers(planDetails?.meals),
    [planDetails]
  );

  // Optimistic: newly added option numbers that may not be in planDetails yet
  // (so the chip shows and stays selected while the add request is in flight).
  const [optimisticOptionNumbers, setOptimisticOptionNumbers] = useState([]);
  const [isAddingOption, setIsAddingOption] = useState(false);

  const planDisplayOptions = useMemo(() => {
    const set = new Set(planAvailableOptions);
    optimisticOptionNumbers.forEach((n) => set.add(String(n)));
    return Array.from(set).sort((a, b) => Number(a) - Number(b));
  }, [planAvailableOptions, optimisticOptionNumbers]);

  useEffect(() => {
    if (optimisticOptionNumbers.length === 0) return;
    setOptimisticOptionNumbers((prev) => {
      const next = prev.filter((n) => planAvailableOptions.includes(String(n)));
      return next.length === prev.length ? prev : next;
    });
  }, [planAvailableOptions, optimisticOptionNumbers]);

  /** Attach cached recipe_details so live nutrition matches the meal editor. */
  const planMealsForNutrition = useMemo(() => {
    const meals = planDetails?.meals;
    if (!meals?.length) return meals;
    return meals.map((meal) => ({
      ...meal,
      meal_options: (meal.meal_options || []).map((option) => ({
        ...option,
        foods: (option.foods || []).map((food) => {
          if (food.recipe_details || food.food_details) return food;
          const id = food.food_id || food.target_food_id || food.actual_food_id;
          const cached = id ? nutritionDataMap[String(id)] : null;
          return cached ? { ...food, recipe_details: cached } : food;
        }),
      })),
    }));
  }, [planDetails?.meals, nutritionDataMap]);

  const { nutritionSummary: livePlanNutritionSummary } = useMealStructureNutrition(
    planMealsForNutrition,
    { enabled: Boolean(plannedData?.planId && planMealsForNutrition?.length) }
  );

  const normalizePlanNutritionSummary = (raw) => {
    if (!raw || typeof raw !== "object") return null;
    return {
      ...raw,
      totalsByOption:
        raw.totalsByOption || raw.totals_by_option || raw.byOption || null,
      averageAcrossOptions:
        raw.averageAcrossOptions || raw.average_across_options || null,
      byMealType: raw.byMealType || raw.by_meal_type || null,
    };
  };

  const activePlanNutritionSummary = useMemo(() => {
    const fromApi = normalizePlanNutritionSummary(planDetails?.nutrition_summary);
    const live = livePlanNutritionSummary;
    // Live undercounts when meal foods lack scalable details. Only prefer it
    // when it meets or beats API totals (optimistic adds / full local scale).
    const liveSum = sumSummaryCalories(live);
    const apiSum = sumSummaryCalories(fromApi);
    // A macro-poor API summary must not win on calories alone — that renders
    // correct kcal with every macro card at 0g.
    if (live && liveSum > 0 && summaryHasMacros(live) && !summaryHasMacros(fromApi)) {
      return live;
    }
    if (live && liveSum > 0 && liveSum >= apiSum) return live;
    return fromApi || live;
  }, [planDetails?.nutrition_summary, livePlanNutritionSummary]);

  const [planToolbarNutrition, setPlanToolbarNutrition] = useState(() => ({
    averageAcrossOptions: emptyCompactTotals(),
    selectedContext: emptyCompactTotals(),
  }));

  useEffect(() => {
    const active = activePlanNutritionSummary;
    if (!active) {
      setPlanToolbarNutrition({
        averageAcrossOptions: emptyCompactTotals(),
        selectedContext: emptyCompactTotals(),
      });
      return;
    }
    const totalsByOption = active.totalsByOption || active.byOption || {};
    const averageAcrossOptions =
      active.averageAcrossOptions ||
      averageCompactTotalsAcrossOptions(totalsByOption);
    let selectedContext = { ...emptyCompactTotals() };

    if (planViewMode === "options-first" && selectedOptionFilter) {
      selectedContext = toCompactNutrientTotals(
        totalsByOption[selectedOptionFilter] ??
          totalsByOption[`Option ${selectedOptionFilter}`] ??
          {}
      );
    } else if (planViewMode === "meal-types-first" && selectedMealTypeFilter) {
      const mealKey = Object.keys(active.byMealType || {}).find(
        (k) => normalizeKey(k) === normalizeKey(selectedMealTypeFilter)
      );
      const opts = mealKey ? active.byMealType[mealKey] : null;
      if (opts && Object.keys(opts).length) {
        selectedContext = averageCompactTotalsAcrossOptions(opts);
      }
    }

    setPlanToolbarNutrition({
      averageAcrossOptions: toCompactNutrientTotals(averageAcrossOptions || {}),
      selectedContext: toCompactNutrientTotals(selectedContext),
    });
  }, [
    activePlanNutritionSummary,
    planViewMode,
    selectedOptionFilter,
    selectedMealTypeFilter,
  ]);

  const planDisplayMeta = useMemo(() => {
    const planId = plannedData.planId || planIdProp;
    if (!planId) {
      return { title: "Plan", startStr: "", endStr: "" };
    }
    const meta = planMeta[planId] || {};
    const durations = planDetails?.durations || [];
    const duration =
      (meta.durationId
        ? durations.find((d) => d.id === meta.durationId)
        : null) || durations[0];
    return {
      title: meta.title || planDetails?.title || "Plan",
      startStr: toYYYYMMDD(
        meta.active_from ?? duration?.active_from ?? duration?.start_date
      ),
      endStr: toYYYYMMDD(
        meta.active_to ?? duration?.active_to ?? duration?.end_date
      ),
    };
  }, [plannedData.planId, planIdProp, planMeta, planDetails]);

  useEffect(() => {
    if (!planDetails || planViewMode !== "options-first") return;
    const opts = planAvailableOptions;
    if (opts.length > 0 && !opts.includes(selectedOptionFilter)) {
      setSelectedOptionFilter(String(opts[0]));
    }
  }, [planDetails, planViewMode, planAvailableOptions, selectedOptionFilter]);

  // Must be async and RETURN the fetch: the meal-slot editor awaits this
  // before releasing its optimistic markers. Returning undefined made that
  // await a no-op and edits reverted against stale plan data.
  const handlePlanPeriodDataChange = useCallback(async ({
    skipPlanFetch = false,
    planData = null,
    patchFood = null,
    removeFood = null,
  } = {}) => {
    const seedDetails = (meals) => {
      const detailsMap = buildMealFoodDetailsMap(meals || []);
      if (Object.keys(detailsMap).length) {
        setNutritionDataMap((prev) => ({ ...prev, ...detailsMap }));
      }
    };

    // Optimistic meal-tree edits must not wait on fetch guards — they drive
    // immediate toolbar / row calorie updates.
    if (removeFood) {
      if (plannedData.planId) planIdToPreserveRef.current = plannedData.planId;
      setPlanDetails((prev) => {
        if (!prev?.meals) return prev;
        const scaled = removeFood.scaled || removeFood.food?.scaled_nutrition;
        return {
          ...prev,
          meals: removeFoodFromMeals(prev.meals, removeFood),
          nutrition_summary: bumpNutritionSummary(prev.nutrition_summary, {
            periodName: removeFood.periodName,
            optionTab: removeFood.optionTab,
            scaled,
            sign: -1,
          }),
        };
      });
      return;
    }

    if (patchFood?.food) {
      if (plannedData.planId) planIdToPreserveRef.current = plannedData.planId;
      setPlanDetails((prev) => {
        if (!prev?.meals) return prev;
        const existing = findFoodInMeals(prev.meals, patchFood);
        let nutrition_summary = prev.nutrition_summary;
        if (existing?.scaled_nutrition) {
          nutrition_summary = bumpNutritionSummary(nutrition_summary, {
            periodName: patchFood.periodName,
            optionTab: patchFood.optionTab,
            scaled: existing.scaled_nutrition,
            sign: -1,
          });
        }
        nutrition_summary = bumpNutritionSummary(nutrition_summary, {
          periodName: patchFood.periodName,
          optionTab: patchFood.optionTab,
          scaled: patchFood.food.scaled_nutrition,
          sign: 1,
        });
        return {
          ...prev,
          meals: upsertFoodInMeals(prev.meals, patchFood),
          nutrition_summary,
        };
      });
      const details = patchFood.food.recipe_details;
      const fid = patchFood.food.food_id;
      if (fid && details) {
        setNutritionDataMap((prev) => ({ ...prev, [String(fid)]: details }));
      }
      return;
    }

    if (!activePlanId || !dietEventTypeId || !profileId) return false;
    planIdToPreserveRef.current = activePlanId;

    if (planData) {
      // Keep API nutrition_summary as fallback when live scaling lacks details.
      setPlanDetails(planData);
      seedDetails(planData.meals);
      return true;
    }

    if (!skipPlanFetch) {
      const result = await fetchTargetPlanDetails(
        dietEventTypeId,
        activePlanId,
        profileId
      );
      if (result?.success && result?.data?.data) {
        setPlanDetails(result.data.data);
        seedDetails(result.data.data.meals);
        return true;
      }
      return false;
    }
    return false;
  }, [activePlanId, dietEventTypeId, profileId]);

  const planMealTypeIdMap = useMemo(() => {
    const map = {};
    (dietCommonUtils?.meal_types || []).forEach((mt) => {
      const d = (mt.description || "").trim().toLowerCase();
      if (d) map[d] = mt.id;
    });
    return map;
  }, [dietCommonUtils?.meal_types]);

  const handleAddMealTypeToPlan = useCallback(
    async (mealTypeDescription, optionNumToAdd = null) => {
      if (!dietEventTypeId || !plannedData.planId) return;
      let meal_type_id = planMealTypeIdMap[(mealTypeDescription || "").trim().toLowerCase()];
      if (!meal_type_id && dietCommonUtils?.meal_types?.length) {
        const key = normalizeKey(mealTypeDescription || "");
        const mt = dietCommonUtils.meal_types.find((m) => normalizeKey(m.description || "") === key);
        if (mt?.id) meal_type_id = mt.id;
      }
      if (!meal_type_id) {
        setToastMessage("Meal type not found.");
        setToastType("error");
        return;
      }
      try {
        // The API accepts a second meal row of the same type and nothing here
        // would reveal it: buildMealDataByPeriod keys by meal type and the later
        // row wins, hiding the earlier one's foods from every plan screen. Add
        // to the meal that already exists rather than creating a twin.
        const existingMeal = (planDetails?.meals || []).find(
          (m) => String(m.meal_type_id) === String(meal_type_id)
        );
        if (existingMeal?.id) {
          if (!optionNumToAdd) {
            setToastMessage("This meal type is already in the plan.");
            setToastType("warning");
            return;
          }
          const addOptRes = await modifyTargetPlanMealOptions({
            event_type_id: dietEventTypeId,
            meal_options: [{
              operation: "ADD",
              target_plan_meal_id: existingMeal.id,
              title: `Option ${optionNumToAdd}`,
              description: "",
            }],
          });
          if (!addOptRes?.success) {
            setToastMessage(addOptRes?.message || "Failed to add option.");
            setToastType("error");
            return;
          }
          setToastMessage(`Option ${optionNumToAdd} added.`);
          setToastType("success");
          await handlePlanPeriodDataChange();
          return;
        }

        const res = await modifyTargetPlanMeals({
          event_type_id: dietEventTypeId,
          meals: [{
            operation: "ADD",
            target_plan_id: plannedData.planId,
            meal_type_id,
            sequence_order: (planDetails?.meals?.length || 0) + 1,
          }],
        });
        if (!res?.success) {
          setToastMessage(res?.message || "Failed to add meal type.");
          setToastType("error");
          return;
        }
        setToastMessage("Meal type added.");
        setToastType("success");
        const result = await fetchTargetPlanDetails(dietEventTypeId, plannedData.planId, profileId);
        const updated = result?.success && result?.data?.data ? result.data.data : null;
        const prevIds = new Set((planDetails?.meals || []).map((m) => m.id));
        if (optionNumToAdd && updated?.meals) {
          const newMeal = updated.meals.find((m) => m.id && !prevIds.has(m.id));
          if (newMeal?.id) {
            const addOpt = await modifyTargetPlanMealOptions({
              event_type_id: dietEventTypeId,
              meal_options: [{ operation: "ADD", target_plan_meal_id: newMeal.id, title: `Option ${optionNumToAdd}`, description: "" }],
            });
            if (addOpt?.success) setToastMessage("Meal type and option added.");
          }
        }
        if (updated) setPlanDetails(updated);
        await handlePlanPeriodDataChange({ skipPlanFetch: Boolean(updated) });
      } catch (e) {
        setToastMessage("Failed to add meal type.");
        setToastType("error");
      }
    },
    [dietEventTypeId, plannedData.planId, profileId, planDetails, planMealTypeIdMap, handlePlanPeriodDataChange, dietCommonUtils?.meal_types]
  );

  const handlePlanAddOptionAll = useCallback(async () => {
    if (!dietEventTypeId || !planDetails?.meals) return;
    const nextNum = planAvailableOptions.length ? String(Math.max(...planAvailableOptions.map(Number)) + 1) : "1";
    const title = `Option ${nextNum}`;
    const payload = MEALPERIODS.map((period) => planMealIdMap[normalizeKey(period)]).filter(Boolean).map((target_plan_meal_id) => ({
      operation: "ADD",
      target_plan_meal_id,
      title,
      description: "",
    }));
    if (payload.length === 0) {
      setToastMessage("No meals found to add option to.");
      setToastType("error");
      return;
    }
    // Show new option in badges immediately and keep it selected (in case refetch is stale)
    setOptimisticOptionNumbers((prev) => (prev.includes(nextNum) ? prev : [...prev, nextNum]));
    setSelectedOptionFilter(nextNum);
    setIsAddingOption(true);
    try {
      const res = await modifyTargetPlanMealOptions({
        event_type_id: dietEventTypeId,
        meal_options: payload,
      });
      if (!res?.success) {
        // Drop the optimistic badge again — the option was never created.
        setOptimisticOptionNumbers((prev) => prev.filter((n) => n !== nextNum));
        setToastMessage(res?.message || "Failed to add option.");
        setToastType("error");
        return;
      }
      await handlePlanPeriodDataChange();
      setSelectedOptionFilter(nextNum);
      setToastMessage(`${title} added. Add meal types and foods below.`);
      setToastType("success");
    } catch (e) {
      setOptimisticOptionNumbers((prev) => prev.filter((n) => n !== nextNum));
      setToastMessage("Failed to add option.");
      setToastType("error");
    } finally {
      setIsAddingOption(false);
    }
  }, [dietEventTypeId, planDetails, planAvailableOptions, planMealIdMap, handlePlanPeriodDataChange]);

  const handlePlanAddOptionToMealType = useCallback(
    async (periodName, optionNum) => {
      if (!dietEventTypeId) return;
      const meal_id = planMealIdMap[normalizeKey(periodName)];
      if (!meal_id) return;
      const title = `Option ${optionNum}`;
      try {
        const res = await modifyTargetPlanMealOptions({
          event_type_id: dietEventTypeId,
          meal_options: [{ operation: "ADD", target_plan_meal_id: meal_id, title, description: "" }],
        });
        if (res?.success) {
          setToastMessage(`${title} added to ${periodName}.`);
          setToastType("success");
          handlePlanPeriodDataChange();
        } else {
          setToastMessage(res?.message || "Failed to add option.");
          setToastType("error");
        }
      } catch (e) {
        setToastMessage("Failed to add option.");
        setToastType("error");
      }
    },
    [dietEventTypeId, planMealIdMap, handlePlanPeriodDataChange]
  );

  // Save a meal's intake window ({ start, end } in "HH:MM", "" = clear)
  // on the target plan meal, as-is with no timezone conversion; "" clears a field.
  const handleUpdateMealIntakeWindow = useCallback(
    async (periodName, { start, end }) => {
      if (!dietEventTypeId || !activePlanId) return;
      const mealId = planMealIdMap[normalizeKey(periodName)];
      if (!mealId) return;
      const intake_start_time = hhmmForApi(start);
      const intake_end_time = hhmmForApi(end);
      const previous = (planDetails?.meals || []).find((m) => m.id === mealId);
      const patchMeal = (fields) =>
        setPlanDetails((prev) =>
          prev?.meals
            ? { ...prev, meals: prev.meals.map((m) => (m.id === mealId ? { ...m, ...fields } : m)) }
            : prev
        );
      if (plannedData.planId) planIdToPreserveRef.current = plannedData.planId;
      patchMeal({
        intake_start_time: intake_start_time || null,
        intake_end_time: intake_end_time || null,
      });
      const revert = (message) => {
        patchMeal({
          intake_start_time: previous?.intake_start_time ?? null,
          intake_end_time: previous?.intake_end_time ?? null,
        });
        setToastMessage(message || "Failed to update intake window.");
        setToastType("error");
      };
      try {
        const res = await modifyTargetPlanMeals({
          event_type_id: dietEventTypeId,
          // The API rejects any meal row without its target_plan_id, UPDATE included.
          meals: [{
            operation: "UPDATE",
            id: mealId,
            target_plan_id: activePlanId,
            intake_start_time,
            intake_end_time,
          }],
        });
        if (!res?.success) {
          revert(res?.message);
          return;
        }
        setToastMessage(
          intake_start_time || intake_end_time
            ? `${periodName} intake window updated.`
            : `${periodName} intake window cleared.`
        );
        setToastType("success");
      } catch (e) {
        revert();
      }
    },
    [dietEventTypeId, activePlanId, planMealIdMap, planDetails, plannedData.planId]
  );

  const requestPlanDeleteOptionConfirm = useCallback(
    (optionNum) => {
      const matches = findMealOptionsByNumber(planDetails?.meals, optionNum);
      const sectionCount = matches.length;
      const foodCount = matches.reduce((sum, m) => sum + m.foodsCount, 0);
      // Only empty options (no recipes in any meal type) can be deleted for now.
      if (foodCount > 0) {
        setToastMessage(`Option ${optionNum} has recipes and can't be deleted.`);
        setToastType("warning");
        return;
      }
      // Deleting a non-trailing option would leave a numbering gap (renumbering
      // isn't supported — the API silently no-ops title renames). Only the
      // highest-numbered option can be deleted, so numbers stay contiguous.
      const maxOption = planAvailableOptions.length ? Math.max(...planAvailableOptions.map(Number)) : null;
      if (maxOption !== null && Number(optionNum) !== maxOption) {
        setToastMessage(`Only the last option can be deleted. Delete Option ${maxOption} first.`);
        setToastType("warning");
        return;
      }
      const message = `Delete Option ${optionNum}? ${sectionCount} meal section${sectionCount === 1 ? "" : "s"
        } will be removed from this plan.`;
      setOptionDeleteConfirm({ open: true, optionNum, message });
    },
    [planDetails?.meals, planAvailableOptions, setToastMessage, setToastType]
  );

  const handleCancelDeletePlanOption = useCallback(() => {
    setOptionDeleteConfirm({ open: false, optionNum: null, message: "" });
  }, []);

  const handleConfirmDeletePlanOption = useCallback(async () => {
    const { optionNum } = optionDeleteConfirm;
    if (!optionNum || !dietEventTypeId) return;
    if (isDeletingOption) return;
    const matches = findMealOptionsByNumber(planDetails?.meals, optionNum);
    if (matches.length === 0) {
      setOptionDeleteConfirm({ open: false, optionNum: null, message: "" });
      setToastMessage("Option no longer exists.");
      setToastType("warning");
      return;
    }
    // Re-check: a recipe could have been added to this option while the confirm was open.
    if (matches.some((m) => m.foodsCount > 0)) {
      setOptionDeleteConfirm({ open: false, optionNum: null, message: "" });
      setToastMessage(`Option ${optionNum} has recipes and can't be deleted.`);
      setToastType("warning");
      return;
    }
    // Re-check: a higher option could have been added elsewhere while the confirm was open.
    const maxOption = planAvailableOptions.length ? Math.max(...planAvailableOptions.map(Number)) : null;
    if (maxOption !== null && Number(optionNum) !== maxOption) {
      setOptionDeleteConfirm({ open: false, optionNum: null, message: "" });
      setToastMessage(`Only the last option can be deleted. Delete Option ${maxOption} first.`);
      setToastType("warning");
      return;
    }
    setIsDeletingOption(true);
    try {
      const res = await modifyTargetPlanMealOptions({
        event_type_id: dietEventTypeId,
        meal_options: matches.map((m) => ({
          operation: "DELETE",
          id: m.optionId,
          target_plan_meal_id: m.mealId,
        })),
      });
      if (res?.success) {
        setToastMessage("Option deleted.");
        setToastType("success");
        handlePlanPeriodDataChange();
      } else {
        setToastMessage(res?.message || "Failed to delete option.");
        setToastType("error");
      }
    } catch (e) {
      setToastMessage("Failed to delete option.");
      setToastType("error");
    } finally {
      setIsDeletingOption(false);
      setOptionDeleteConfirm({ open: false, optionNum: null, message: "" });
    }
  }, [optionDeleteConfirm, dietEventTypeId, planDetails?.meals, planAvailableOptions, handlePlanPeriodDataChange, isDeletingOption]);

  const transformTotalsForCompactCard = (totals) => ({
    calories: totals?.calories ?? 0,
    proteins: totals?.proteins ?? 0,
    carbs: totals?.carbs ?? 0,
    fats: totals?.fats ?? 0,
    fiber: totals?.fiber ?? 0,
    sugars: totals?.sugars ?? 0,
  });

  // -------------------- Helper Functions --------------------

  // Define meal type order
  const MEAL_TYPE_ORDER = [
    "PRE-BREAKFAST",
    "BREAKFAST",
    "MID-MORNING SNACK",
    "LUNCH",
    "EVENING SNACK",
    "SNACKS",
    "DINNER",
    "BEDTIME",
    "POST-WORKOUT",
    "PRE-WORKOUT",
  ];

  const sortMealTypesByOrder = (mealTypes) => {
    return mealTypes.sort((a, b) => {
      const indexA = MEAL_TYPE_ORDER.indexOf(a.toUpperCase());
      const indexB = MEAL_TYPE_ORDER.indexOf(b.toUpperCase());

      // If meal type is not in predefined order, put it at the end
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });
  };

  const updateDateRange = useCallback(() => {
    const today = new Date();
    const dates = [];
    // Generate dates from 10 days before to 10 days after (matches tracking API window)
    for (let i = -10; i <= 10; i++) {
      const newDate = new Date(today);
      newDate.setDate(newDate.getDate() + i);
      dates.push(newDate);
    }
    setVisibleDates(dates);
  }, []);

  const registerMealFoodsForDetails = (foods = [], map = {}, missingIds = []) => {
    foods.forEach((food) => {
      if (!food?.food_id) return;
      const key = String(food.food_id);
      const detail = getRecipeDetails(food);
      if (detail) map[key] = detail;
      else if (!map[key]) missingIds.push(key);
    });
    return map;
  };

  const emptyMacroTotals = () => ({
    calories: 0,
    proteins: 0,
    carbs: 0,
    fats: 0,
    fiber: 0,
    sugars: 0,
  });

  const buildTrackedMeals = (actualTrackings, nutritionMap, scaledMap = {}) => {
    if (!actualTrackings || !Array.isArray(actualTrackings)) {
      return { meals: {}, meal_type_totals: {}, meal_windows: {} };
    }

    const trackedMeals = {};
    // Meal type → intake window (local "HH:MM"), read-only from the linked plan meal.
    const mealWindows = {};

    actualTrackings.forEach((trackingRecord) => {
      const mealsArray = trackingRecord.meals || [];

      mealsArray.forEach((meal) => {
        const mealType = meal.tracking_meal_type || "MEAL";
        const mealFoods = meal.meal_foods || meal.foods || [];
        const mealWindow = getMealIntakeWindow(meal);
        if (!mealWindows[mealType] && (mealWindow.start || mealWindow.end)) {
          mealWindows[mealType] = mealWindow;
        }

        if (!trackedMeals[mealType]) {
          trackedMeals[mealType] = {};
        }

        if (mealFoods.length > 0) {
          const foodsTransformed = mealFoods.map((raw) => {
            const food = mealFoodFromTrackingActual(raw);
            if (!food) return null;
            const key = String(food.food_id);
            const data = nutritionMap[key];
            const inlineDetail = getRecipeDetails(food);
            const detail = data || inlineDetail;

            return {
              food_id: food.food_id,
              food_name:
                getRecipeName(detail) ||
                `Recipe ${key.slice(0, 8)}`,
              quantity: food.quantity ?? food.target_quantity ?? 1,
              quantity_type: food.quantity_type ?? food.target_quantity_type ?? "g",
              scaled_nutrition: scaledMap[String(food.food_id)] || null,
              food_details: {
                main_details: {
                  calories_calculated_for: detail?.calories_calculated_for ?? 100,
                },
                nutrition: nutrientsToDisplayObject(detail?.nutrients),
                measures: detail?.measures || [],
              },
              _original: raw,
            };
          }).filter(Boolean);

          const existingOptions = Object.keys(trackedMeals[mealType]);
          const nextOptionNumber =
            existingOptions.length > 0
              ? Math.max(...existingOptions.map(Number)) + 1
              : 1;

          trackedMeals[mealType][nextOptionNumber] = foodsTransformed;
        }
      });
    });

    return {
      meals: trackedMeals,
      meal_type_totals: {},
      meal_windows: mealWindows,
    };
  };

  const buildPlannedMeals = (targetPlans, nutritionMap, scaledMap = {}) => {
    if (!targetPlans || targetPlans.length === 0)
      return { meals: {}, meal_type_totals: {} };

    const plan = targetPlans[0];
    const mealsArray = plan.meals || [];
    const plannedMeals = {};
    const mealWindows = {};

    mealsArray.forEach((meal) => {
      const mealType = meal.meal_type || meal.meal_type_description || "default";
      plannedMeals[mealType] = {};
      mealWindows[mealType] = getMealIntakeWindow(meal);

      (meal.meal_options || []).forEach((option, idx) => {
        const optionNum = String(idx + 1);
        const foodsTransformed = (option.foods || []).map((raw) => {
          const food = mealFoodFromTrackingTarget(raw);
          if (!food) return null;
          const planFoodId = raw.target_plan_meal_option_food_id ?? raw.id;
          const data = nutritionMap[food.food_id];

          const quantityType = food.target_quantity_type ?? food.target_quantity_type_description ?? food.quantity_type ?? "g";
          return {
            food_id: food.food_id,
            food_name:
              getRecipeName(data) ||
              `Recipe ${food.food_id?.slice(0, 8)}`,
            quantity: raw.target_quantity ?? raw.quantity,
            quantity_type: quantityType,
            scaled_nutrition: scaledMap[String(food.food_id)] || null,
            deleteId: planFoodId,
            id: planFoodId,
            food_details: {
              main_details: {
                calories_calculated_for: data?.calories_calculated_for ?? 100,
              },
              nutrition: nutrientsToDisplayObject(data?.nutrients),
              measures: data?.measures || [],
            },
            _original: raw,
          };
        }).filter(Boolean);
        plannedMeals[mealType][optionNum] = foodsTransformed;
      });
    });

    return {
      meals: plannedMeals,
      meal_type_totals: plan.meal_type_totals || {},
      meal_windows: mealWindows,
    };
  };


  // Plan date range: which dates have the current plan, total days
  const _planDateRange = useMemo(() => {
    if (!isPlannedView || !plannedData?.planId || !Object.keys(allNutritionData).length) return null;
    const planId = plannedData.planId;
    const planDates = Object.keys(allNutritionData)
      .filter((d) => allNutritionData[d]?.plannedPlanId === planId)
      .sort();
    if (planDates.length === 0) return null;
    const startDate = planDates[0];
    const endDate = planDates[planDates.length - 1];
    const currentIndex = planDates.indexOf(selectedDate);
    const currentDayIndex = currentIndex >= 0 ? currentIndex + 1 : null;
    return {
      planDates,
      totalDays: planDates.length,
      startDate,
      endDate,
      currentDayIndex,
    };
  }, [isPlannedView, plannedData?.planId, selectedDate, allNutritionData]);

  // All distinct plans (for switching between current and previous plans). Newest first.
  const _allPlansList = useMemo(() => {
    if (!isPlannedView || !Object.keys(allNutritionData).length) return [];
    const byPlanId = {};
    Object.entries(allNutritionData).forEach(([date, data]) => {
      const planId = data?.plannedPlanId;
      if (!planId) return;
      if (!byPlanId[planId]) byPlanId[planId] = [];
      byPlanId[planId].push(date);
    });
    return Object.entries(byPlanId)
      .map(([planId, dates]) => {
        const sorted = [...dates].sort();
        return {
          planId,
          startDate: sorted[0],
          endDate: sorted[sorted.length - 1],
          planDates: sorted,
          totalDays: sorted.length,
        };
      })
      .sort((a, b) => a.startDate.localeCompare(b.startDate));
  }, [isPlannedView, allNutritionData]);

  const formatDateForDisplay = formatScheduleDate;

  // -------------------- Initial Data Fetching (Called Once) --------------------
  const fetchAllNutritionData = useCallback(async () => {
    try {
      if (isInitialLoad) {
        setIsLoading(true);
      } else {
        setIsTrackingLoading(true);
      }
      const clientId = profileId;
      const trackingEventTypeId = eventTypeId || dietEventTypeId;
      const recipeLookupEventTypeId = recipeEventTypeId || dietEventTypeId;

      // Get date range for API call
      const today = new Date();
      const startDate = formatDate(
        new Date(today.getTime() - 10 * 24 * 60 * 60 * 1000)
      );
      const endDate = formatDate(
        new Date(today.getTime() + 10 * 24 * 60 * 60 * 1000)
      );

      if (!clientId || !trackingEventTypeId) {
        throw new Error("Missing required parameters.");
      }


      // Preset nutrients + tracking in parallel (preset only once).
      let settingsResponse = null;
      let response;
      const trackingPromise = fetchDailyDietTrackingDetails(
        trackingEventTypeId,
        startDate,
        endDate,
        clientId
      );
      if (!presetNutritionFetchedRef.current) {
        const [preset, tracking] = await Promise.all([
          fetchPresetNutrition(eventTypeId, profileId),
          trackingPromise,
        ]);
        settingsResponse = preset;
        presetNutritionFetchedRef.current = true;
        response = tracking;
      } else {
        response = await trackingPromise;
      }

      const allData = Array.isArray(response?.data?.data)
        ? response.data.data
        : response?.data?.data
        ? [response.data.data]
        : [];

      const nutritionMap = {};
      const missingRecipeIds = [];
      allData.forEach((dateData) => {
        (dateData.actual_trackings || []).forEach((tracking) => {
          (tracking.meals || []).forEach((meal) => {
            const rows = (meal.meal_foods || meal.foods || [])
              .map(mealFoodFromTrackingActual)
              .filter(Boolean);
            registerMealFoodsForDetails(rows, nutritionMap, missingRecipeIds);
          });
        });
        (dateData.target_plans || []).forEach((plan) => {
          (plan.meals || []).forEach((meal) => {
            (meal.meal_options || []).forEach((option) => {
              const rows = (option.foods || [])
                .map(mealFoodFromTrackingTarget)
                .filter(Boolean);
              registerMealFoodsForDetails(rows, nutritionMap, missingRecipeIds);
            });
          });
        });
      });
      // Do not block first paint on full-recipe hydrate — backfill effect fills gaps.
      setNutritionDataMap((prev) => ({ ...prev, ...nutritionMap }));

      const processedData = {};
      for (const dateData of allData) {
        if (!dateData.date) continue;

        const plannedScaledMap = scaledNutritionMapFromTargetPlans(
          dateData.target_plans
        );
        const trackedScaledMap = scaledNutritionMapFromActualTrackings(
          dateData.actual_trackings
        );

        const trackedMeals = buildTrackedMeals(
          dateData.actual_trackings,
          nutritionMap,
          trackedScaledMap
        );
        const plannedMeals = buildPlannedMeals(
          dateData.target_plans,
          nutritionMap,
          plannedScaledMap
        );

        processedData[dateData.date] = {
          original: dateData,
          trackedMeals,
          plannedMeals,
          plannedPlanId:
            dateData.target_plans?.[0]?.target_plan_id ??
            dateData.target_plans?.[0]?.id ??
            null,
          trackedTotals:
            dateData.tracked_nutrition_totals || emptyMacroTotals(),
          plannedTotals:
            dateData.planned_nutrition_totals || emptyMacroTotals(),
        };
      }

      setAllNutritionData(processedData);

      // Set nutrition settings
      setNutritionData((prev) => ({
        ...prev,
        settings: settingsResponse || prev.settings || {
          calories: 0,
          protein: 0,
          carbs: 0,
          fats: 0,
          fiber: 0,
          sugar: 0,
        },
        totals: prev.totals || {
          calories: 0,
          proteins: 0,
          carbs: 0,
          fats: 0,
          fiber: 0,
          sugars: 0,
        },
      }));

      // Non-blocking hydrate — mark attempted so the backfill effect skips the same ids.
      const uniqueMissing = [...new Set(missingRecipeIds)];
      if (uniqueMissing.length > 0 && recipeLookupEventTypeId) {
        uniqueMissing.forEach((id) =>
          trackedRecipeHydrationAttemptedRef.current.add(id)
        );
        hydrateMissingRecipeDetails(uniqueMissing, recipeLookupEventTypeId).then(
          (fetched) => {
            if (fetched && Object.keys(fetched).length) {
              setNutritionDataMap((prev) => ({ ...prev, ...fetched }));
            }
          }
        );
      }

    
    } catch (err) {
      console.error("Error fetching all nutrition data:", err);
      setToastMessage(err.message || "Failed to fetch nutrition data");
      setToastType("error");
      trackingFetchedRef.current = false;
    } finally {
      setIsLoading(false);
      setIsTrackingLoading(false);
      setIsInitialLoad(false);
    }
  }, [get, profileId, eventTypeId, dietEventTypeId, recipeEventTypeId, isInitialLoad]);

  // -------------------- Filter Data for Selected Date --------------------
  const filterDataForDate = useCallback(
    (date) => {
      const dateData = allNutritionData[date];

      if (!dateData) {
        // No data for this date
        setNutritionData((prev) => ({
          ...prev,
          meals: {},
          totals: {},
          meal_type_totals: {},
        }));
        // Keep the URL plan (and the one we just edited). This branch always
        // runs on the plan detail page — it never populates allNutritionData —
        // and clearing planId here used to disable every refetch on that page.
        setPlannedData({
          meals: {},
          meal_type_totals: {},
          planId: planIdProp ?? planIdToPreserveRef.current ?? null,
        });
        return;
      }

      if (!isPlannedView) {
        // Set tracked data
        setNutritionData((prev) => ({
          ...prev,
          meals: dateData.trackedMeals.meals,
          totals: dateData.trackedTotals,
          meal_type_totals: dateData.trackedMeals.meal_type_totals,
          meal_windows: dateData.trackedMeals.meal_windows,
        }));
        // Reset planned data
        setPlannedData({
          meals: null,
          meal_type_totals: null,
          planId: planIdProp ?? null,
        });
      } else {
        // Set planned data with totals and planId for planned view.
        // Preserve current plan: URL plan (planIdProp), or plan we just edited (planIdToPreserveRef), so saving a food item doesn't switch to another plan.
        const planIdToUse = planIdProp ?? planIdToPreserveRef.current ?? dateData.plannedPlanId ?? null;
        if (planIdToPreserveRef.current != null) planIdToPreserveRef.current = null;
        setPlannedData({
          ...dateData.plannedMeals,
          totals: dateData.plannedTotals,
          planId: planIdToUse,
        });

        // Update nutrition data with planned totals for the card
        setNutritionData((prev) => ({
          ...prev,
          totals: dateData.plannedTotals,
        }));

        // Set default expanded options for planned view
        const defaultOptions = {};
        Object.keys(dateData.plannedMeals.meals).forEach((mealType) => {
          const firstOption =
            Object.keys(dateData.plannedMeals.meals[mealType])[0] || "1";
          defaultOptions[mealType] = firstOption;
        });
        setExpandedOptions(defaultOptions);
      }
    },
    [allNutritionData, isPlannedView, planIdProp]
  );

  // -------------------- Effects --------------------
  useEffect(() => {
    updateDateRange();
  }, [updateDateRange]);

  // Initial paint: never block the full-page loader on ±10-day tracking - only
  // isInitialLoad needs clearing for that (the loader guard is `isLoading &&
  // isInitialLoad`). Leave isLoading alone: it's the accurate "tracking fetch
  // in flight" signal fetchAllNutritionData sets/clears, which the Plan vs
  // Goal card's loading skeleton depends on. Clearing it here too raced with
  // that fetch's own setIsLoading(true) under StrictMode's double-invoked
  // mount effects, permanently stuck it at false, and made the card flash "0"
  // with no loading indicator for the whole fetch instead of a skeleton.
  useEffect(() => {
    if (!isInitialLoad || !profileId || !(eventTypeId || dietEventTypeId)) return;
    setIsInitialLoad(false);
  }, [
    isInitialLoad,
    profileId,
    eventTypeId,
    dietEventTypeId,
  ]);

  // Preset goals for Nutrition Information card — independent of Tracked fetch.
  // The plan detail page never runs fetchAllNutritionData (it is Planned-first),
  // so this is the only path that loads goals there. Claim the ref on success
  // rather than on start: claiming it up front makes StrictMode's mount →
  // unmount → mount cycle cancel the first fetch and skip the second, leaving
  // goals permanently at 0.
  useEffect(() => {
    if (!profileId || !eventTypeId || presetNutritionFetchedRef.current) return;
    let cancelled = false;
    fetchPresetNutrition(eventTypeId, profileId)
      .then((preset) => {
        if (cancelled || !preset) return;
        presetNutritionFetchedRef.current = true;
        setNutritionData((prev) => ({
          ...prev,
          settings: preset,
        }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [profileId, eventTypeId]);

  // If already on Tracked when the client changes, reload immediately.
  useEffect(() => {
    if (isPlannedView || !profileId || !(eventTypeId || dietEventTypeId)) return;
    if (trackingFetchedRef.current) return;
    trackingFetchedRef.current = true;
    fetchAllNutritionData();
  }, [isPlannedView, profileId, eventTypeId, dietEventTypeId, fetchAllNutritionData]);

  // Backfill recipe names for tracked foods once catalog event type is available
  useEffect(() => {
    const recipeLookupEventTypeId = recipeEventTypeId || dietEventTypeId;
    if (!recipeLookupEventTypeId || !Object.keys(allNutritionData).length) return;

    const missingIds = [];
    Object.values(allNutritionData).forEach((dateData) => {
      (dateData.original?.actual_trackings || []).forEach((tracking) => {
        (tracking.meals || []).forEach((meal) => {
          (meal.meal_foods || meal.foods || []).forEach((raw) => {
            const id = raw?.actual_food_id;
            if (!id) return;
            const key = String(id);
            const existing = nutritionDataMap[key];
            if (!existing || !getRecipeName(existing)) missingIds.push(key);
          });
        });
      });
    });

    const uniqueMissing = [...new Set(missingIds)].filter(
      (id) => !trackedRecipeHydrationAttemptedRef.current.has(id)
    );
    if (!uniqueMissing.length) return;
    uniqueMissing.forEach((id) => trackedRecipeHydrationAttemptedRef.current.add(id));

    let cancelled = false;
    (async () => {
      const fetched = await hydrateMissingRecipeDetails(
        uniqueMissing,
        recipeLookupEventTypeId
      );
      if (cancelled || !Object.keys(fetched).length) return;
      setNutritionDataMap((prev) => ({ ...prev, ...fetched }));
    })();

    return () => {
      cancelled = true;
    };
  }, [allNutritionData, nutritionDataMap, recipeEventTypeId, dietEventTypeId]);

  // Load diet common utils when in plan view (for plan update/delete quantity type mapping)
  useEffect(() => {
    if (isPlannedView && (plannedData.meals || planDetails) && !dietCommonUtils) {
      fetchDietCommonUtils?.();
    }
  }, [isPlannedView, plannedData.meals, planDetails, dietCommonUtils, fetchDietCommonUtils]);

  // When plan details load, hydrate recipe_details for meal foods (embedded on Diet API).
  useEffect(() => {
    if (!isPlannedView || !planDetails?.meals) return;
    let cancelled = false;
    (async () => {
      const detailsMap = await buildMealFoodDetailsMap(
        planDetails.meals,
        nutritionDataMap
      );
      if (!cancelled) {
        setNutritionDataMap((p) => ({ ...p, ...detailsMap }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPlannedView, planDetails]);

  // Filter data when date, view, or underlying data changes (e.g. after plan update/delete)
  useEffect(() => {
    if (!isInitialLoad && selectedDate) {
      filterDataForDate(selectedDate);
    }
  }, [selectedDate, isPlannedView, filterDataForDate, isInitialLoad, allNutritionData]);

  // In Planned view: plans table comes from fetchTargetPlanSummaries (no dates); user clicks row to view details.

  const refetchPlansTable = useCallback((clientId) => {
    const idToUse = clientId ?? profileId;
    if (!dietEventTypeId || !idToUse) return Promise.resolve();
    return fetchTargetPlanSummaries(dietEventTypeId, null, null, idToUse).then((res) => {
      if (res?.success && Array.isArray(res?.data?.data?.target_plans)) {
        setPlansTableList(flattenTargetPlanSummaries(res.data.data.target_plans));
        setPlansTableError("");
      } else {
        setPlansTableList([]);
        setPlansTableError(res?.message || "Failed to load diet plans.");
      }
    }).catch(() => {
      setPlansTableList([]);
      setPlansTableError("Failed to load diet plans. Please try again.");
    });
  }, [dietEventTypeId, profileId]);

  const handleConfirmDeletePlan = useCallback(async () => {
    if (!planToDelete || !dietEventTypeId) return;
    setIsDeletingPlan(true);
    try {
      const res = await modifyTargetPlans({
        event_type_id: dietEventTypeId,
        target_plans: [{ operation: "DELETE", id: planToDelete }],
      });
      if (res?.success) {
        setToastMessage("Plan deleted.");
        setToastType("success");
        if (plannedData?.planId === planToDelete) {
          setPlannedData((prev) => ({ ...prev, planId: null }));
          setPlanDetails(null);
          if (isPlanDetailPage) {
            navigate(`/dashboard/${profileId}`);
            return;
          }
        }
        refetchPlansTable();
      } else {
        setToastMessage(res?.message || "Failed to delete plan.");
        setToastType("error");
      }
    } catch (e) {
      setToastMessage("Failed to delete plan.");
      setToastType("error");
    } finally {
      setIsDeletingPlan(false);
      setPlanToDelete(null);
      setDeleteConfirmMessage("");
    }
  }, [planToDelete, dietEventTypeId, plannedData?.planId, refetchPlansTable, isPlanDetailPage, navigate, profileId]);

  const handleCancelDeletePlan = useCallback(() => {
    setPlanToDelete(null);
    setDeleteConfirmMessage("");
  }, []);

  const handleSaveEditPlan = useCallback(async () => {
    if (!dietEventTypeId || !editingPlanId) return;
    setEditPlanError("");
    setEditPlanSaving(true);
    try {
      const planId = editingPlanId;
      const meta = planMeta[planId];
      const nameTrim = editPlanName.trim();
      const duplicateName = plansTableList.some(
        (p) => p.id !== planId && (p.title || "").toLowerCase().trim() === nameTrim.toLowerCase()
      );
      if (duplicateName) {
        setEditPlanError("A plan with this name already exists. Please choose a different name.");
        return;
      }
      if (editPlanStartDate >= editPlanEndDate) {
        setEditPlanError("End date must be after start date.");
        return;
      }
      const summariesRes = await fetchTargetPlanSummaries(
        dietEventTypeId,
        editPlanStartDate,
        editPlanEndDate,
        profileId
      );
      if (summariesRes.success) {
        const plans = summariesRes.data?.data?.target_plans || [];
        if (plans.some((p) => p.id && String(p.id) !== String(planId))) {
          setEditPlanError("A plan already exists for this date range. Please choose different dates.");
          return;
        }
      }
      const titleRes = await modifyTargetPlans({
        event_type_id: dietEventTypeId,
        target_plans: [{
          operation: "UPDATE",
          id: planId,
          title: nameTrim,
          description: planDetails?.description ?? meta?.description ?? "",
          assigned_to_id: profileId,
        }],
      });
      if (!titleRes.success) {
        setEditPlanError(titleRes.message || "Failed to update plan name");
        return;
      }
      if (meta?.durationId) {
        const fromDate = parseLocalDate(editPlanStartDate);
        const toDate = parseLocalDate(editPlanEndDate);
        if (!fromDate || !toDate) {
          setEditPlanError("Please enter valid dates.");
          return;
        }
        const durRes = await modifyTargetPlanDurations({
          event_type_id: dietEventTypeId,
          durations: [{
            operation: "UPDATE",
            id: meta.durationId,
            target_plan_id: planId,
            active_from: `${formatLocalDate(fromDate)}T00:00:00`,
            active_to: `${formatLocalDate(toDate)}T00:00:00`,
          }],
        });
        if (!durRes.success) {
          setEditPlanError(durRes.message || "Failed to update plan dates");
          return;
        }
      }
      setToastMessage("Plan updated successfully");
      setToastType("success");
      setPlanMeta((prev) => ({
        ...prev,
        [planId]: { ...prev[planId], title: nameTrim, active_from: editPlanStartDate, active_to: editPlanEndDate },
      }));
      setEditPlanModalOpen(false);
      setEditPlanError("");
      handlePlanPeriodDataChange();
      await refetchPlansTable(profileId);
    } catch (err) {
      setEditPlanError(err?.message || "Failed to update plan");
    } finally {
      setEditPlanSaving(false);
    }
  }, [
    dietEventTypeId, editingPlanId, editPlanName, plansTableList, editPlanStartDate, editPlanEndDate,
    profileId, planMeta, planDetails, handlePlanPeriodDataChange, fetchAllNutritionData, refetchPlansTable,
  ]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (!isPlannedView) setVisibleDates(updateVisibleDates());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isPlannedView]);

  // Toast timeout
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  // Show success when returning after assigning a diet plan
  useEffect(() => {
    if (location.state?.dietPlanAssigned) {
      setToastMessage("Diet plan assigned successfully.");
      setToastType("success");
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.dietPlanAssigned, location.pathname, navigate]);

  // -------------------- Handlers --------------------
  const openDietPlanPdf = useCallback(
    async ({ planId, planData } = {}) => {
      if (!profileId || !dietEventTypeId) {
        setToastMessage("Missing client or diet event type for PDF.");
        setToastType("error");
        return;
      }
      if (!eventTypeId) {
        setToastMessage("Session is still loading. Please try again in a moment.");
        setToastType("error");
        return;
      }
      if (!planData && !planId) {
        setToastMessage("No plan selected for PDF.");
        setToastType("error");
        return;
      }

      setIsDownloadingPDF(true);
      try {
        const result = await generateDietPlanPdf({
          profileEventTypeId: eventTypeId,
          dietEventTypeId,
          profileId,
          planId,
          planData,
          fetchTargetPlanDetails,
          getUserProfileDetails,
          fetchPresetNutrition,
          existingDetailsMap: nutritionDataMap,
          empId: userId,
          get,
          returnBlob: true,
          planTitle: planData?.title || planMeta[planId || planIdProp]?.title,
        });

        if (!result.success) {
          setToastMessage(result.message || "Failed to generate PDF");
          setToastType("error");
          return;
        }

        if (result.blob) {
          const url = URL.createObjectURL(result.blob);
          setPdfBlobUrl(url);
          setPdfFilename(result.filename || "diet-plan.pdf");
          setPdfModalOpen(true);
          setToastMessage("PDF ready to view");
          setToastType("success");
        } else {
          setToastMessage("PDF generated but could not be displayed.");
          setToastType("error");
        }
      } catch (err) {
        console.error("PDF generation error:", err);
        setToastMessage(err?.message || "Failed to generate PDF");
        setToastType("error");
      } finally {
        setIsDownloadingPDF(false);
      }
    },
    [
      eventTypeId,
      profileId,
      dietEventTypeId,
      nutritionDataMap,
      get,
      userId,
      planMeta,
      planIdProp,
    ]
  );

  const handleViewPDF = useCallback(async () => {
    const activePlanId = plannedData?.planId || planIdProp;
    if (!activePlanId) {
      setToastMessage("No plan selected for PDF.");
      setToastType("error");
      return;
    }
    if (!planDetails) {
      setToastMessage("Plan is still loading. Please try again.");
      setToastType("error");
      return;
    }
    await openDietPlanPdf({ planData: planDetails, planId: activePlanId });
  }, [
    openDietPlanPdf,
    plannedData?.planId,
    planIdProp,
    planDetails,
  ]);

  const handleViewPDFForPlan = useCallback(
    async (planId) => {
      await openDietPlanPdf({ planId });
    },
    [openDietPlanPdf]
  );

  const handleClonePlan = useCallback((plan) => {
    if (!plan?.id) return;
    setCopySourcePlan(plan);
  }, []);

  const handleCloseCopyModal = useCallback(() => {
    if (cloningPlanId) return;
    setCopySourcePlan(null);
  }, [cloningPlanId]);

  const handleCopyPlanContinue = useCallback(
    async ({ planName, startDate, endDate }) => {
      const plan = copySourcePlan;
      if (!plan?.id || !dietEventTypeId || !profileId) return;
      setCloningPlanId(plan.id);
      try {
        const prefetched = await prefetchTargetPlanDetails(
          dietEventTypeId,
          plan.id,
          profileId
        );
        setCopySourcePlan(null);
        navigate(`/diet/plans`, {
          state: {
            openCreatePlan: true,
            sourcePlanId: plan.id,
            sourcePlanTitle: plan.title || "",
            suggestedPlanName: planName,
            prefetchedSourcePlan: prefetched,
            fromUserPage: true,
            clientId: profileId,
            returnTo,
            fromDate: startDate,
            toDate: endDate,
          },
        });
      } catch (e) {
        console.error("Failed to start plan copy:", e);
        setToastMessage("Failed to load plan for copying.");
        setToastType("error");
      } finally {
        setCloningPlanId(null);
      }
    },
    [copySourcePlan, dietEventTypeId, profileId, navigate, returnTo]
  );

  const handleOpenSaveAsTemplate = useCallback(() => {
    if (!activePlanId) return;
    setSaveAsTemplateError("");
    setPartialTemplateId(null);
    setSavingAsTemplatePlan({ id: activePlanId, title: planDisplayMeta?.title || "" });
    if (dietEventTypeId) {
      fetchTemplateSummaries(dietEventTypeId).then((res) => {
        const templates = res?.data?.data?.templates || [];
        setExistingTemplateNames(templates.map((t) => t.title || "").filter(Boolean));
      });
    }
  }, [activePlanId, planDisplayMeta, dietEventTypeId]);

  const handleCloseSaveAsTemplateModal = useCallback(() => {
    if (savingAsTemplate) return;
    setSavingAsTemplatePlan(null);
    setSaveAsTemplateError("");
    setPartialTemplateId(null);
  }, [savingAsTemplate]);

  // fromPartialSave stops the template editor from seeding empty options into
  // the half-built template on open — the coach came here to see what actually
  // made it across, not to have the gaps papered over.
  const handleOpenPartialTemplate = useCallback(
    (templateId) => {
      if (!templateId) return;
      setSavingAsTemplatePlan(null);
      navigate(`/diet/templates/${templateId}`, {
        state: { fromCreate: true, fromPartialSave: true },
      });
    },
    [navigate]
  );

  const handleSaveAsTemplateContinue = useCallback(
    async ({ name, description, securityId }) => {
      const plan = savingAsTemplatePlan;
      if (!plan?.id || !dietEventTypeId || !profileId) return;
      setSavingAsTemplate(true);
      setSaveAsTemplateError("");
      setPartialTemplateId(null);
      try {
        const result = await saveTargetPlanAsTemplate({
          eventTypeId: dietEventTypeId,
          sourcePlanId: plan.id,
          clientId: profileId,
          title: name,
          description,
          securityId,
          onPhase: setSaveAsTemplatePhase,
        });
        if (result.ok && result.templateId) {
          setSavingAsTemplatePlan(null);
          setToastMessage("Template created from this plan.");
          setToastType("success");
          navigate(`/diet/templates/${result.templateId}`, { state: { fromCreate: true } });
        } else {
          setSaveAsTemplateError(result.message || "Failed to save plan as template.");
          setPartialTemplateId(result.templateId || null);
        }
      } finally {
        setSavingAsTemplate(false);
        setSaveAsTemplatePhase(null);
      }
    },
    [savingAsTemplatePlan, dietEventTypeId, profileId, navigate, setToastMessage, setToastType]
  );

  const plansTableColumns = useMemo(() => [
    {
      accessorKey: "title",
      header: "Plan name",
      size: 240,
      minSize: 160,
      enableSorting: true,
      enableFiltering: false,
      cell: (info) => (
        <span className="font-medium text-gray-900 truncate block" title={info.getValue() || ""}>
          {info.getValue() || "Unnamed plan"}
        </span>
      ),
    },
    {
      accessorKey: "start_date",
      header: "Start date",
      size: 120,
      minSize: 90,
      enableSorting: true,
      enableFiltering: false,
      cell: (info) => (
        <span className="text-sm text-gray-700">{formatDateForDisplay(info.getValue() || "")}</span>
      ),
    },
    {
      accessorKey: "end_date",
      header: "End date",
      size: 120,
      minSize: 90,
      enableSorting: true,
      enableFiltering: false,
      cell: (info) => (
        <span className="text-sm text-gray-700">{formatDateForDisplay(info.getValue() || "")}</span>
      ),
    },
    {
      accessorKey: "edit",
      header: "Edit",
      size: 80,
      minSize: 70,
      enableSorting: false,
      enableFiltering: false,
      cell: (info) => {
        const plan = info.row.original;
        return (
          <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => {
                setEditingPlanId(plan.id);
                setEditPlanName(plan.title || "");
                setEditPlanStartDate(toYYYYMMDD(plan.start_date) || "");
                setEditPlanEndDate(toYYYYMMDD(plan.end_date) || "");
                setEditPlanError("");
                setPlanMeta((prev) => ({
                  ...prev,
                  [plan.id]: {
                    title: plan.title || "",
                    durationId: plan.duration_id,
                    active_from: plan.start_date,
                    active_to: plan.end_date,
                    description: plan.description ?? "",
                  },
                }));
                setEditPlanModalOpen(true);
              }}
              className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              title="Edit plan"
              aria-label="Edit plan"
            >
              <Pencil className="w-4 h-4" />
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: "copy",
      header: "Copy",
      size: 90,
      minSize: 80,
      enableSorting: false,
      enableFiltering: false,
      cell: (info) => {
        const plan = info.row.original;
        return (
          <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => handleClonePlan(plan)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-700"
              title="Copy plan"
              aria-label="Copy plan"
            >
              <Copy className="w-4 h-4 text-orange-primary" />
              Copy
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: "delete",
      header: "Delete",
      size: 80,
      minSize: 70,
      enableSorting: false,
      enableFiltering: false,
      cell: (info) => {
        const plan = info.row.original;
        return (
          <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => {
                setPlanToDelete(plan.id);
                setDeleteConfirmMessage("Are you sure you want to delete this plan?");
              }}
              className="p-2 rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors"
              title="Delete plan"
              aria-label="Delete plan"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: "view_pdf",
      header: "View PDF",
      size: 100,
      minSize: 85,
      enableSorting: false,
      enableFiltering: false,
      cell: (info) => {
        const plan = info.row.original;
        return (
          <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => handleViewPDFForPlan(plan.id)}
              disabled={isDownloadingPDF}
              className="p-2 rounded-lg text-orange-600 hover:text-orange-700 hover:bg-orange-50 transition-colors disabled:opacity-50"
              title="View PDF"
              aria-label="View PDF"
            >
              <FileText className="w-4 h-4" />
            </button>
          </div>
        );
      },
    },
  ], [handleViewPDFForPlan, handleClonePlan, isDownloadingPDF]);

  const closePdfModal = useCallback(() => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setPdfFilename("");
    setPdfModalOpen(false);
  }, [pdfBlobUrl]);

  const handleDownloadPdfFromModal = useCallback(() => {
    if (!pdfBlobUrl || !pdfFilename) return;
    const a = document.createElement("a");
    a.href = pdfBlobUrl;
    a.download = pdfFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setToastMessage("PDF downloaded");
    setToastType("success");
  }, [pdfBlobUrl, pdfFilename]);

  const handleChangeTracked = useCallback(() => {
    setIsPlannedView(false);
    if (!trackingFetchedRef.current) {
      trackingFetchedRef.current = true;
      fetchAllNutritionData();
    }
  }, [fetchAllNutritionData]);

  // Same fetch gate as Tracked click — used on hover to hide latency.
  const prefetchTrackingData = useCallback(() => {
    if (trackingFetchedRef.current) return;
    trackingFetchedRef.current = true;
    fetchAllNutritionData();
  }, [fetchAllNutritionData]);

  const handleChangePlanned = () => setIsPlannedView(true);
  const handleOnSelectDate = (date) => {
    setSelectedDate(date);
  };
  const handleChangeDiet = () => {
    const fromDate = new Date();
    const toDate = new Date();
    toDate.setDate(toDate.getDate() + 7);

    // Assign/create client plan — stay on plans editor, not Templates library
    navigate(`/diet/plans`, {
      state: {
        fromUserPage: true,
        openCreatePlan: true,
        clientId: profileId,
        returnTo,
        fromDate,
        toDate,
      },
    });
  };
  const toggleOption = (mealType, option) =>
    setExpandedOptions((prev) => ({ ...prev, [mealType]: option }));

  // -------------------- Render Meal Section (Unified UI) --------------------
  const renderMealSection = (
    mealsData,
    openState,
    setOpenState,
    isTracked = false,
    // Meal type → intake window ({ start, end } local "HH:MM"), display only.
    mealWindows = {}
  ) => {
    if (!mealsData || Object.keys(mealsData).length === 0) {
      return (
        <div className="text-center py-14 bg-gray-50 rounded-xl border border-gray-200">
          <svg
            className="w-20 h-20 mx-auto mb-4 text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <p className="text-gray-600 text-xl font-semibold mb-2">
            {isTracked
              ? "No tracked meals for this date"
              : "No meal plans found for this date"}
          </p>
          <p className="text-gray-400 text-sm">
            {isTracked
              ? "Start tracking your meals to see them here"
              : "Create a meal plan to see it here"}
          </p>
        </div>
      );
    }

    // For tracked view, filter out meal types with no foods
    let filteredMealsData = mealsData;
    if (isTracked) {
      filteredMealsData = {};
      Object.keys(mealsData).forEach((mealType) => {
        const mealOptions = mealsData[mealType];
        // Check if any option has foods
        const hasFoods = Object.values(mealOptions).some(
          (foods) => Array.isArray(foods) && foods.length > 0
        );
        if (hasFoods) {
          filteredMealsData[mealType] = mealOptions;
        }
      });

      // If no meal types have foods, show empty state
      if (Object.keys(filteredMealsData).length === 0) {
        return (
          <div className="text-center py-14 bg-gray-50 rounded-xl border border-gray-200">
            <svg
              className="w-20 h-20 mx-auto mb-4 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <p className="text-gray-600 text-xl font-semibold mb-2">
              No tracked meals for this date
            </p>
            <p className="text-gray-400 text-sm">
              Start tracking your meals to see them here
            </p>
          </div>
        );
      }
    }

    // Sort meal types according to predefined order
    const sortedMealTypes = sortMealTypesByOrder(
      Object.keys(filteredMealsData)
    );

    return sortedMealTypes.map((mealType) => {
      const isOpen = openState[mealType] || false;
      const mealOptions = mealsData[mealType] || {};

      return (
        <div
          key={mealType}
          className="mb-6 border border-gray-200 rounded-xl shadow-sm bg-white hover:shadow-md transition-shadow duration-200"
        >
          <button
            onClick={() =>
              setOpenState((prev) => ({
                ...prev,
                [mealType]: !prev[mealType],
              }))
            }
            className="w-full flex justify-between items-center px-5 py-4 hover:bg-orange-50 transition-all duration-200 rounded-t-xl group"
          >
            <div className="flex items-center gap-3">
              <strong className="text-gray-800 text-base font-semibold group-hover:text-orange-600 transition-colors">
                {mealType.toUpperCase()}
              </strong>
              {formatIntakeWindowLabel(mealWindows?.[mealType]) && (
                <span className="text-xs text-gray-500 tabular-nums">
                  {formatIntakeWindowLabel(mealWindows[mealType])}
                </span>
              )}
              {/* <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full group-hover:bg-orange-100 group-hover:text-orange-600 transition-colors">
                {Object.keys(mealOptions).length}{" "}
                {Object.keys(mealOptions).length === 1 ? "Food" : "Foods"}
              </span> */}
            </div>
            <span
              className={`text-orange-500 text-lg transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
            >
              ▼
            </span>
          </button>

          {isOpen && (
            <div className="px-5 py-4 border-t border-gray-100 bg-gray-50">
              {/* Option Selector - Only for Planned View */}
              {!isTracked && (
                <div className="flex flex-wrap gap-2 mb-5">
                  {Object.keys(mealOptions).map((option) => {
                    const isSelected = expandedOptions[mealType] === option;
                    const optionFoods = mealOptions[option];
                    const hasFood =
                      Array.isArray(optionFoods) && optionFoods.length > 0;

                    return (
                      <button
                        key={option}
                        className={`px-4 py-2 rounded-lg transition-all duration-200 font-medium text-sm ${
                          isSelected
                            ? "bg-orange-500 text-white shadow-md scale-105"
                            : hasFood
                            ? "bg-white hover:bg-orange-50 text-orange-primary border border-orange-200 hover:border-orange-300 shadow-sm"
                            : "bg-white hover:bg-gray-100 text-gray-600 border border-gray-200 shadow-sm"
                        }`}
                        onClick={() => toggleOption(mealType, option)}
                      >
                        Option {option}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Food Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(() => {
                  // For tracked view, show all foods from all options
                  if (isTracked) {
                    const allFoods = [
                      ...new Set(Object.values(mealOptions).flat()),
                    ];

                    if (allFoods.length > 0) {
                      return allFoods.map((food, index) => {
                        const display = resolveTrackedFoodDisplay(food, nutritionDataMap);
                        return (
                        <NutritionFoodCard
                          key={food.food_id || `${display.foodName}-${index}`}
                          foodName={display.foodName}
                          quantity={display.quantity}
                          quantityType={display.quantityType}
                          scaledNutrition={food.scaled_nutrition}
                        />
                        );
                      });
                    } else {
                      return (
                        <div className="col-span-full text-center py-10 bg-white rounded-lg border border-dashed border-gray-300">
                          <svg
                            className="w-16 h-16 mx-auto mb-3 text-gray-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                            />
                          </svg>
                          <p className="text-gray-500 text-base font-medium">
                            No tracked foods
                          </p>
                        </div>
                      );
                    }
                  }

                  // For planned view, show selected option foods
                  const selectedOption =
                    expandedOptions[mealType] ||
                    Object.keys(mealOptions)[0] ||
                    "1";
                  const selectedFoods = mealOptions[selectedOption];

                  if (
                    Array.isArray(selectedFoods) &&
                    selectedFoods.length > 0
                  ) {
                    return selectedFoods.map((food, index) => (
                      <NutritionFoodCard
                        key={food.recipe_id || `${food.food_name}-${index}`}
                        foodName={food.food_name}
                        quantity={food.quantity}
                        quantityType={food.quantity_type}
                        scaledNutrition={food.scaled_nutrition}
                      />
                    ));
                  } else {
                    return (
                      <div className="col-span-full text-center py-10 bg-white rounded-lg border border-dashed border-gray-300">
                        <svg
                          className="w-16 h-16 mx-auto mb-3 text-gray-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                        <p className="text-gray-500 text-base font-medium">
                          No planned foods for Option {selectedOption}
                        </p>
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  return {
    profileId, planIdProp, initialPlanMeta, isPlanDetailPage, visibleDates, setVisibleDates,
    selectedDate, setSelectedDate, get, userId, location, allNutritionData,
    setAllNutritionData, nutritionDataMap, setNutritionDataMap, nutritionData, setNutritionData,
    plannedData, setPlannedData, activePlanId, isPlannedView, setIsPlannedView, isLoading, isTrackingLoading, isInitialLoad,
    expandedOptions, setExpandedOptions, openTracked, setOpenTracked, openPlanned, setOpenPlanned,
    toastMessage, setToastMessage, toastType, setToastType, navigate, dietCommonUtils,
    fetchDietCommonUtils, eventTypeId, dietEventTypeId, planDetails, setPlanDetails,
    planDetailsLoading, selectedOptionFilter, setSelectedOptionFilter, planViewMode, setPlanViewMode,
    selectedMealTypeFilter, setSelectedMealTypeFilter, addChoiceModal, setAddChoiceModal, planMeta,
    setPlanMeta, editPlanModalOpen, setEditPlanModalOpen, editingPlanId, setEditingPlanId,
    editPlanName, setEditPlanName, editPlanStartDate, setEditPlanStartDate, editPlanEndDate,
    setEditPlanEndDate, editPlanSaving, setEditPlanSaving, editPlanError, setEditPlanError,
    isDownloadingPDF, setIsDownloadingPDF, pdfModalOpen, setPdfModalOpen, pdfBlobUrl, setPdfBlobUrl,
    pdfFilename, setPdfFilename, plansTableList, setPlansTableList, plansTableLoading,
    plansTableError, setPlansTableError,
    setPlansTableLoading, planToDelete, setPlanToDelete, deleteConfirmMessage, setDeleteConfirmMessage,
    isDeletingPlan, cloningPlanId, copySourcePlan,
    savingAsTemplatePlan, savingAsTemplate, saveAsTemplatePhase, saveAsTemplateError,
    partialTemplateId, existingTemplateNames,
    handleOpenSaveAsTemplate, handleCloseSaveAsTemplateModal, handleSaveAsTemplateContinue,
    handleOpenPartialTemplate,
    planIdToPreserveRef, planDataByPeriod, planMealIdMap, planAvailableOptions,
    handlePlanPeriodDataChange, planMealTypeIdMap,
    planMealWindows, handleUpdateMealIntakeWindow,
    handleAddMealTypeToPlan, handlePlanAddOptionAll, handlePlanAddOptionToMealType,
    planEmptyOptions, planDisplayOptions, isAddingOption,
    optionDeleteConfirm, isDeletingOption, requestPlanDeleteOptionConfirm,
    handleConfirmDeletePlanOption, handleCancelDeletePlanOption,
    transformTotalsForCompactCard, MEAL_TYPE_ORDER, sortMealTypesByOrder, updateDateRange,
    planSummary: activePlanNutritionSummary,
    planToolbarNutrition,
    planDisplayMeta,
    formatDateForDisplay,
    fetchAllNutritionData,
    filterDataForDate,
    refetchPlansTable, handleConfirmDeletePlan, handleCancelDeletePlan, handleViewPDF,
    handleViewPDFForPlan, handleSaveEditPlan, handleClonePlan, handleCloseCopyModal,
    handleCopyPlanContinue,
    plansTableColumns, closePdfModal, handleDownloadPdfFromModal,
    handleChangeTracked, prefetchTrackingData, handleChangePlanned, handleOnSelectDate, handleChangeDiet, toggleOption,
    renderMealSection, MEALPERIODS,
  };
}

export { DietPlanTabContext };
